import React, { useState } from "react";

// Premium brand badge rendering with user-uploaded image support and high-fidelity HTML/CSS fallback
export const BrandBadge = ({ name, color = "#00C853", size = "md", loading = "lazy" }) => {
    const norm = name.toLowerCase().replace(/[\s\-_]+/g, "");
    
    // Normalize to standard url-friendly slug
    // e.g. "Parions Sport" -> "parions-sport", "PMU Play" -> "pmu-play"
    const slug = name
        .toLowerCase()
        .trim()
        .replace(/[\s_]+/g, "-")
        .replace(/-+/g, "-");

    const [imgError, setImgError] = useState(false);

    const sizes = {
        sm: "h-9 px-3 text-xs rounded-md min-w-[90px]",
        md: "h-11 px-4 text-sm rounded-lg min-w-[120px]",
        lg: "h-14 px-6 text-lg rounded-xl min-w-[150px]",
    };

    // If image exists and hasn't failed to load, display it transparently and borderless
    if (!imgError) {
        return (
            <div
                data-testid={`brand-badge-${name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`inline-flex items-center justify-center select-none ${sizes[size]}`}
            >
                <img
                    src={`/images/logos/${slug}.png`}
                    alt={`${name} logo`}
                    loading={loading}
                    onError={() => setImgError(true)}
                    className="object-contain w-auto h-full max-w-full transition-transform duration-200 hover:scale-105"
                />
            </div>
        );
    }

    // HTML/CSS fallback logos if image fails to load
    const getFallbackElement = () => {
        if (norm === "winamax") {
            return (
                <div className="flex items-center gap-1.5 font-black tracking-tighter">
                    <span className="bg-[#E60028] text-white w-5 h-5 rounded-full flex items-center justify-center text-[11px] not-italic border border-white/20 shadow-sm">W</span>
                    <span className="text-white font-extrabold italic uppercase tracking-tighter">WINAMAX</span>
                </div>
            );
        }
        if (norm === "betclic") {
            return (
                <div className="flex items-center font-black italic tracking-tighter text-white">
                    <span className="text-[#E60028] bg-white px-1.5 py-0.5 rounded font-black tracking-tight not-italic mr-1 text-[11px]">B</span>
                    <span className="font-extrabold uppercase">Betclic</span>
                </div>
            );
        }
        if (norm === "unibet") {
            return (
                <div className="flex gap-0.5 items-center">
                    {["U", "N", "I", "B", "E", "T"].map((char, idx) => (
                        <div
                            key={idx}
                            className="w-4.5 h-4.5 rounded-full bg-white text-[#005A36] flex items-center justify-center font-black text-[10px] shadow-sm"
                            style={{ width: "17px", height: "17px" }}
                        >
                            {char}
                        </div>
                    ))}
                </div>
            );
        }
        if (norm === "zebet") {
            return (
                <div className="flex items-center font-black tracking-tight text-white leading-none">
                    <span className="bg-[#FFF000] text-black px-1.5 py-1 rounded-l text-[10px] font-black">ZE</span>
                    <span className="bg-[#1E1E1E] text-white px-1.5 py-1 rounded-r text-[10px] font-black border-y border-r border-white/10">bet</span>
                </div>
            );
        }
        if (norm === "parionssport" || norm === "parions") {
            return (
                <div className="flex flex-col items-center leading-none">
                    <span className="text-[#00A859] font-black tracking-tighter text-[9px] uppercase">PARIONS</span>
                    <span className="text-white font-black tracking-tighter text-[12px] uppercase">SPORT</span>
                </div>
            );
        }
        if (norm.startsWith("pmu")) {
            return (
                <div className="flex items-center gap-1 font-black italic tracking-tighter text-white">
                    <span className="text-white font-extrabold uppercase">PMU</span>
                    <span className="text-[#8CC63F] text-xs not-italic">★</span>
                </div>
            );
        }
        if (norm === "bet365") {
            return (
                <div className="flex items-center font-bold tracking-tight text-[#FFDF1B] italic">
                    bet<span className="text-white not-italic font-extrabold">365</span>
                </div>
            );
        }
        if (norm === "bwin") {
            return (
                <div className="flex items-center font-black tracking-tighter italic">
                    <span className="text-white uppercase">b</span>
                    <span className="text-[#FFCC00] lowercase font-extrabold">win</span>
                </div>
            );
        }
        if (norm.includes("pokerstars")) {
            return (
                <div className="flex items-center gap-1.5 font-extrabold text-white">
                    <span className="text-[#E60028] text-sm">★</span>
                    <span className="tracking-tight text-xs">PokerStars</span>
                </div>
            );
        }
        if (norm === "vbet") {
            return (
                <div className="flex items-center font-black text-white tracking-tight lowercase">
                    v<span className="text-[#00C853]">bet</span>
                </div>
            );
        }
        if (norm === "betsson") {
            return (
                <div className="flex items-center font-black tracking-tight text-white uppercase italic">
                    betsson
                </div>
            );
        }
        if (norm === "netbet") {
            return (
                <div className="flex items-center font-black tracking-tight text-white">
                    <span className="text-[#E31E24]">Net</span>
                    <span className="text-white">Bet</span>
                </div>
            );
        }
        if (norm === "daznbet") {
            return (
                <div className="flex items-center font-black tracking-widest text-white italic">
                    DAZN <span className="text-[#E7FF00] font-bold not-italic ml-1 text-[10px]">BET</span>
                </div>
            );
        }
        if (norm === "genybet") {
            return (
                <div className="flex items-center font-black tracking-tight text-white">
                    geny<span className="text-[#8CC63F]">bet</span>
                </div>
            );
        }
        if (norm === "circusbet") {
            return (
                <div className="flex items-center font-black tracking-tight text-white">
                    Circus<span className="text-[#E31E24] ml-0.5">Bet</span>
                </div>
            );
        }
        if (norm === "olybet") {
            return (
                <div className="flex items-center font-black tracking-tight text-white uppercase italic">
                    Oly<span className="text-[#FFF000]">Bet</span>
                </div>
            );
        }
        if (norm === "feelingbet") {
            return (
                <div className="flex items-center font-black tracking-tight text-white">
                    Feeling<span className="text-[#E60028] ml-0.5">bet</span>
                </div>
            );
        }
        if (norm === "yesorno") {
            return (
                <div className="flex items-center font-black tracking-tight text-white uppercase">
                    Yes<span className="text-gray-400">/</span>No
                </div>
            );
        }

        return name.toUpperCase();
    };

    const brandColors = {
        winamax: "linear-gradient(135deg, #1A1A1A, #2B2B2B)",
        betclic: "linear-gradient(135deg, #E60028, #B3001E)",
        unibet: "linear-gradient(135deg, #005A36, #004026)",
        zebet: "linear-gradient(135deg, #000000, #1A1A1A)",
        parionssport: "linear-gradient(135deg, #002F6C, #001A3E)",
        pmu: "linear-gradient(135deg, #006633, #004D26)",
        bet365: "linear-gradient(135deg, #127A5B, #0D5C44)",
        bwin: "linear-gradient(135deg, #000000, #1A1A1A)",
        pokerstars: "linear-gradient(135deg, #0A0A0A, #222222)",
        vbet: "linear-gradient(135deg, #E5127D, #B00A5D)",
        betsson: "linear-gradient(135deg, #F95F00, #C24A00)",
        netbet: "linear-gradient(135deg, #1E1E1E, #2D2D2D)",
        daznbet: "linear-gradient(135deg, #111111, #222222)",
        genybet: "linear-gradient(135deg, #007AC2, #005D94)",
        circusbet: "linear-gradient(135deg, #0A0A0A, #1A1A1A)",
        olybet: "linear-gradient(135deg, #E20613, #B1040E)",
        feelingbet: "linear-gradient(135deg, #1F0813, #3D1025)",
        yesorno: "linear-gradient(135deg, #1E3A5F, #12223A)",
    };

    const gradient = brandColors[norm] || `linear-gradient(135deg, ${color}, ${color}cc)`;

    return (
        <div
            data-testid={`brand-badge-${name.toLowerCase().replace(/\s+/g, "-")}`}
            className={`inline-flex items-center justify-center shadow-md select-none transition-all duration-200 border border-white/10 ${sizes[size]}`}
            style={{
                background: gradient,
                textShadow: "0 1px 2px rgba(0,0,0,0.35)",
            }}
        >
            {getFallbackElement()}
        </div>
    );
};

export default BrandBadge;
